import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ImageLightbox - Full-screen image viewer with navigation
 * Features: Click to view, arrow keys to navigate, ESC to close
 */
const ImageLightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex || 0);

  useEffect(() => {
    const handleNext = () => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrev = () => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length, onClose]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-300"
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all z-10"
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Main image */}
      <div className="relative w-full max-w-5xl max-h-[85vh] flex items-center justify-center">
        <img
          src={images[currentIndex]?.url || images[currentIndex]}
          alt={`${currentIndex + 1} of ${images.length}`}
          className="max-w-full max-h-full object-contain animate-in fade-in duration-300"
        />

        {/* Previous button */}
        {images.length > 1 && (
          <button
            onClick={handlePrev}
            className="absolute left-0 -translate-x-16 sm:-translate-x-20 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all hover:scale-110 active:scale-95"
            aria-label="Previous image"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        {/* Next button */}
        {images.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-0 translate-x-16 sm:translate-x-20 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all hover:scale-110 active:scale-95"
            aria-label="Next image"
          >
            <ChevronRight size={28} />
          </button>
        )}
      </div>

      {/* Thumbnail preview */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 overflow-x-auto px-4">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all border-2 ${
                idx === currentIndex
                  ? 'border-prose-gold scale-110'
                  : 'border-white/30 hover:border-white/60 opacity-60 hover:opacity-100'
              }`}
              aria-label={`Go to image ${idx + 1}`}
            >
              <img
                src={img?.url || img}
                alt={`${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

    </div>
  );
};

export default ImageLightbox;
